import { AdviceProductDetailsFormValues } from "Components/Features/CheckPrice/Products/Advice/ProductDetails/FormElements/interfaces";
import { mapInsuranceNameToAdviceCoverageName } from "Components/Features/CheckPrice/Products/Advice/ProductPriceAka/AkaAdviceCards/mappers";
import { AkaAdvicePriceFormValues } from "Features/CheckPrice/Products/Advice/ProductPriceAka/AkaAdviceCards/interfaces";
import { ProductBackend } from "Store/addProductToCart/productMappers";
import { AdvicePriceCalculationResult } from "Store/akaAdvice/interfaces/AkaAdviceState";
import { ProductName } from "Store/akaAdvice/interfaces/CollectiveAdvice";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { Partner } from "Store/partners/partnerInterfaces";
import { agreementFromPartnerId } from "Store/partners/partnerUtils";
import { UserIdentificationData } from "Store/userIdentification";
import { mapToAkaCriticalIllnessProduct } from "./mapToAkaCriticalIllnessProduct";
import { mapToAkaGroupLifeProduct } from "./mapToAkaGroupLifeProduct";
import { mapToCriticalIllnessProduct } from "./mapToCriticalIllnessProduct";
import { mapToDeathProduct } from "./mapToDeathProduct";
import { mapToDisabilityProduct } from "./mapToDisabilityProduct";
import { mapPrice } from "./utils";

export interface MappingData {
  detailsFormData: AdviceProductDetailsFormValues;
  priceFormData: AkaAdvicePriceFormValues;
  priceData: AdvicePriceCalculationResult[];
  customerData: UserIdentificationData;
}

export const mapToCartProducts = (
  isAdvisor: boolean,
  partnerId: string,
  mappingData: MappingData,
): { [key: string]: any }[] => {
  const mapProduct = (type: ProductName): { [key: string]: any } =>
    mapFormToProduct(isAdvisor, partnerId, type, {
      ...mappingData,
      priceData: mappingData.priceData.filter((price) =>
        mapInsuranceNameToAdviceCoverageName(type).includes(
          price.configurationName,
        ),
      ),
    });

  const cartProducts: { [key: string]: any }[] = [];
  if (isAkaGroupLifeSelected(mappingData.priceFormData)) {
    cartProducts.push(mapProduct(INSURANCE_NAME_VALUE.AKA_GROUP_LIFE));
  }
  if (isDeathInsuranceSelected(mappingData.priceFormData)) {
    cartProducts.push(mapProduct(INSURANCE_NAME_VALUE.DEATH));
  }
  if (isDisabilityInsuranceSelected(mappingData.priceFormData)) {
    cartProducts.push(mapProduct(INSURANCE_NAME_VALUE.DISABILITY_FLEX));
  }
  if (isAkaCriticalIllnessSelected(mappingData.priceFormData)) {
    cartProducts.push(mapProduct(INSURANCE_NAME_VALUE.AKA_CRITICAL_ILLNESS));
  }
  if (isCriticalIllnessInsuranceSelected(mappingData.priceFormData)) {
    cartProducts.push(mapProduct(INSURANCE_NAME_VALUE.CRITICAL_ILLNESS));
  }
  return cartProducts;
};

const mapFormToProduct = (
  isAdvisor: boolean,
  partnerId: string,
  type: ProductName,
  data: MappingData,
): { [key: string]: any } => {
  const discountData = data.customerData.discountResponse.f2100;
  const agreementProduct =
    partnerId === Partner.STOREBRAND
      ? discountData.agreementProduct
      : agreementFromPartnerId(partnerId);
  const paymentPeriod =
    discountData.paymentPeriod === "" ? null : discountData.paymentPeriod;
  const price = isProductWithComposites(type)
    ? mapPrice(isAdvisor, type, data)
    : data.priceData[0].prices[0];

  return {
    cartId: data.customerData.newCartResponse.cartId,
    productId: "",
    visible: true,
    ...mapFormToProductProperties(isAdvisor, type, data),
    priceSpecifications: {
      price: {
        perMonth: price.monthlyPrice,
        perMonthPrecise: price.monthlyPricePrecise,
        perMonthFull: price.fullMonthlyPrice,
        perMonthFullPrecise: price.fullMonthlyPricePrecise,
        perYear: price.yearlyPrice,
        perYearPrecise: price.yearlyPricePrecise,
        perYearFull: price.fullYearlyPrice,
        perYearFullPrecise: price.fullYearlyPricePrecise,
        memberMonthlyPrice: price.memberMonthlyPrice,
        memberMonthlyPricePrecise: price.memberMonthlyPricePrecise,
        memberYearlyPrice: price.memberYearlyPrice,
        memberYearlyPricePrecise: price.memberYearlyPricePrecise,
        coInsuredMonthlyPrice: price.coInsuredMonthlyPrice,
        coInsuredMonthlyPricePrecise: price.coInsuredMonthlyPricePrecise,
        coInsuredYearlyPrice: price.coInsuredYearlyPrice,
        coInsuredYearlyPricePrecise: price.coInsuredYearlyPricePrecise,
      },
      discount: {
        agreementProduct,
        paymentPeriod,
        isNewInsuranceCustomer: discountData.isNewInsuranceCustomer,
      },
      deductible: {
        code: "BE", // TODO-AKA-ADVICE
        value: "N", // TODO-AKA-ADVICE
      },
    },
  };
};

const mapFormToProductProperties = (
  isAdvisor: boolean,
  type: string,
  data: MappingData,
): ProductBackend | undefined => {
  switch (type) {
    case INSURANCE_NAME_VALUE.AKA_GROUP_LIFE:
      return mapToAkaGroupLifeProduct(data);
    case INSURANCE_NAME_VALUE.AKA_CRITICAL_ILLNESS:
      return mapToAkaCriticalIllnessProduct(data);
    case INSURANCE_NAME_VALUE.CRITICAL_ILLNESS:
      return mapToCriticalIllnessProduct(data);
    case INSURANCE_NAME_VALUE.DEATH:
      return mapToDeathProduct(data);
    case INSURANCE_NAME_VALUE.DISABILITY_FLEX:
      return mapToDisabilityProduct(isAdvisor, data);
    default:
      return undefined;
  }
};

const isProductWithComposites = (type: ProductName): boolean =>
  type === INSURANCE_NAME_VALUE.AKA_GROUP_LIFE ||
  type === INSURANCE_NAME_VALUE.AKA_CRITICAL_ILLNESS ||
  type === INSURANCE_NAME_VALUE.DISABILITY_FLEX;

export const isAkaGroupLifeSelected = (
  formValues: AkaAdvicePriceFormValues,
): boolean =>
  isAkaGroupLifeInsuredSelected(formValues) ||
  isAkaGroupLifeCoInsuredSelected(formValues);

export const isAkaGroupLifeInsuredSelected = (
  formValues: AkaAdvicePriceFormValues,
): boolean =>
  isAkaGroupLifeDeathSelected(formValues) ||
  isAkaGroupLifeIncapacitySelected(formValues) ||
  isAkaGroupLifeTimeSelected(formValues);

export const isAkaGroupLifeCoInsuredSelected = (
  formValues: AkaAdvicePriceFormValues,
): boolean =>
  isAkaGroupLifeDeathCoInsuredSelected(formValues) ||
  isAkaGroupLifeIncapacityCoInsuredSelected(formValues) ||
  isAkaGroupLifeTimeCoInsuredSelected(formValues);

export const isAkaGroupLifeDeathSelected = (
  formValues: AkaAdvicePriceFormValues,
): boolean => formValues.deathCheckbox && formValues.akaGroupLifeDeathCheckbox;

export const isAkaGroupLifeDeathCoInsuredSelected = (
  formValues: AkaAdvicePriceFormValues,
): boolean =>
  formValues.deathCheckbox && !!formValues.akaGroupLifeDeathCoInsuredCheckbox;

export const isAkaGroupLifeIncapacitySelected = (
  formValues: AkaAdvicePriceFormValues,
): boolean =>
  formValues.disabilityCheckbox && formValues.incapacityOfWorkCheckbox;

export const isAkaGroupLifeIncapacityCoInsuredSelected = (
  formValues: AkaAdvicePriceFormValues,
): boolean =>
  formValues.disabilityCheckbox &&
  !!formValues.incapacityOfWorkCoInsuredCheckbox;

export const isAkaGroupLifeTimeSelected = (
  formValues: AkaAdvicePriceFormValues,
): boolean =>
  isAkaGroupLifeIncapacitySelected(formValues) &&
  formValues.timeLimitedDisabilityInterestCheckbox;

export const isAkaGroupLifeTimeCoInsuredSelected = (
  formValues: AkaAdvicePriceFormValues,
): boolean =>
  isAkaGroupLifeIncapacityCoInsuredSelected(formValues) &&
  !!formValues.timeLimitedDisabilityInterestCoInsuredCheckbox;

export const isDeathInsuranceSelected = (
  formValues: AkaAdvicePriceFormValues,
): boolean => formValues.deathCheckbox && formValues.deathInsuredSumCheckbox;

export const isDisabilityInsuranceSelected = (
  formValues: AkaAdvicePriceFormValues,
): boolean =>
  formValues.disabilityCheckbox && formValues.disabilityInsuredSumCheckbox;

export const isDisabilityPaymentAfter3MonthsSelected = (
  formValues: AkaAdvicePriceFormValues,
): boolean =>
  isDisabilityInsuranceSelected(formValues) &&
  !!formValues.disabilityPaymentAfter3Months;

export const isAkaCriticalIllnessSelected = (
  formValues: AkaAdvicePriceFormValues,
): boolean =>
  isAkaCriticalIllnessInsuredSelected(formValues) ||
  isAkaCriticalIllnessCoInsuredSelected(formValues);

export const isAkaCriticalIllnessInsuredSelected = (
  formValues: AkaAdvicePriceFormValues,
): boolean =>
  formValues.criticalIllnessCheckbox &&
  formValues.akaCriticalIllnessCoverageAmountCheckbox;

export const isAkaCriticalIllnessCoInsuredSelected = (
  formValues: AkaAdvicePriceFormValues,
): boolean =>
  formValues.criticalIllnessCheckbox &&
  !!formValues.akaCriticalIllnessCoverageAmountCoInsuredCheckbox;

export const isCriticalIllnessInsuranceSelected = (
  formValues: AkaAdvicePriceFormValues,
): boolean =>
  formValues.criticalIllnessCheckbox &&
  formValues.criticalIllnessInsuredSumCheckbox;
